import React from 'react'
import styles from './SampleGraph.scss'
import _ from 'lodash'
import { parseRawTrackPoints } from 'lib/routeUtils'
import { Set } from 'immutable'
import { getConsole } from 'lib/getConsole'
import { TrackLocation } from 'data/records'
const console = getConsole('common', 'SampleGraph')

const sliceEqual = (a, b) => a && b && a[0] === b[0] && a[1] === b[1]

// basic version working. TODO:
// - update the selected map segment display while a segment drag select is in progress
// - solve the flicker issue that was caused by suspendUpdates for drag-select mode
// - stop segment selection in one sample graph from causing zoom in for another
export class SampleGraph extends React.PureComponent {
  static defaultProps = {
    displayControls: false,
    metricsEnabled: Set(['ele']),
    height: 150,
    dragMode: 'zoom' // can be 'zoom' or 'select'
  }

  constructor (props) {
    super(props)
    this._mapId = Math.random().toString(36).substr(7)
    this._canvasId = Math.random().toString(36).substr(7)
    this._profileId = Math.random().toString(36).substr(7)

    if (props.highlightSlice && props.routes && props.routes.length > 1) {
      throw TypeError("Can't highlight a dataslice when more than one route is being shown.")
    }

    this.throttledResize = _.throttle(this.onResize, 100)
    this._suspendRendering = false
    this.state = {}
  }

  componentDidMount () {
    const { displayControls, dragMode, plotOnly, hasSurface, showSurface } = this.props
    this.clobberMetricPref()
    this._sampleGraph = new window.SampleGraph({
      canvasId: this._canvasId,
      revertToZoomInAfterSelectSegment: false,
      plotOnly,
      displayControls,
      showSurface: hasSurface && showSurface
    })
    const sg = this._sampleGraph
    if (dragMode !== 'zoom') {
      sg.unbindFrom(sg, 'segmentselected', sg._currentDragBehaviorFn)
      sg._currentDragBehaviorFn = () => {} // something (plugin or other) is setting currentDragBehaviorFn after this line is executed
    }
    sg.bindTo(sg, 'segmentselected', this.onSegmentSelected)
    sg.bindTo(sg, 'pointselected', this.onSegmentUnselected)
    sg.bindTo(sg, 'highlight_at_position', this.onPointHighlighted)
    // so this gets called at the same time as segmentselected in
    // dragMode=select, but not called at all in zoom mode
    sg.bindTo(sg, 'dataslice:select', this.onSegmentResized)
    window.addEventListener('resize', this.throttledResize)
    this._div.addEventListener('pleaseExportSVG', this.exportSVG)
    if (this.props.rawPoints) {
      this.renderAll()
    }
  }

  componentWillUnmount () {
    this._sampleGraph.unidentifySegment()
    this._sampleGraph.unbindFrom(this._sampleGraph, 'segmentselected', this.onSegmentSelected)
    this._sampleGraph.unbindFrom(this._sampleGraph, 'pointselected', this.onSegmentUnselected)
    this._sampleGraph.unbindFrom(this._sampleGraph, 'highlight_at_position', this.onPointHighlighted)
    this._sampleGraph.unbindFrom(this._sampleGraph, 'dataslice:select', this.onSegmentResized)
    this._div.removeEventListener('pleaseExportSVG', this.exportSVG)
    window.removeEventListener('resize', this.throttledResize)
  }

  componentDidUpdate (prevProps, prevState) {
    if (this.props.dragMode === 'zoom' && this._suspendRendering) { return }
    // don't interrupt user-driven drag events
    if (this._sampleGraph.dragging) { return }

    const {
      rawPoints,
      highlightPoint,
      highlightSlice,
      metricUnits,
      metricsEnabled,
      zoomedIn,
      hoverSlice,
      rwgpsData,
      hasSurface
    } = this.props

    if (hasSurface && prevProps.metricsEnabled.has('surfaces') !== metricsEnabled.has('surfaces')) {
      // do this manually because samplegraph `metricsEnabled` setter doesn't handle surfaces change
      this._sampleGraph.showSurface(metricsEnabled.has('surfaces'))
    }

    if (metricUnits !== prevProps.metricUnits) {
      this.clobberMetricPref()
      this.renderGraph()
    } else if (rawPoints !== prevProps.rawPoints || rwgpsData !== prevProps.rwgpsData) {
      this.renderGraph()
    } else if (!metricsEnabled.equals(prevProps.metricsEnabled)) {
      this.renderGraph()
    }

    if (highlightPoint !== prevProps.highlightPoint) {
      this.renderHighlightPoint()
    }

    if (!_.isEqual(highlightSlice, prevProps.highlightSlice)) {
      this.renderHighlightSlice()
    }

    if (prevState.zoomSlice !== this.state.zoomSlice) {
      this.renderZoom()
    }

    if (zoomedIn !== prevProps.zoomedIn) {
      this.renderZoomLevel()
    }

    if (hoverSlice !== prevProps.hoverSlice) {
      this.renderHoverSlice()
    }
  }

  onResize = () => {
    const { height } = this.props
    this._div && this._sampleGraph.resize(this._div.clientWidth, height)
  }

  // HACK copied from classic routes/show.js. this breaks the react pattern
  // but that's okay because this is an admin-only function that needs to be
  // all knowing and all powerful.
  exportSVG = () => {
    const svgNode = this._sampleGraph.makeSVG('ele')
    svgNode.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
    // uncomment the following two lines to test more easily
    // svgNode.setAttribute('style', 'position: absolute; top: 20px; left: 20px; background: #fff; z-index: 9999')
    // document.body.appendChild(svgNode)
    const url = `data:image/svg+xml;base64,${window.btoa(svgNode.outerHTML)}`
    const link = document.createElement('a')
    link.download = 'elevation.svg'
    link.href = url
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  renderAll = () => {
    this.renderGraph()
    this.renderHighlightSlice()
    this.renderHighlightPoint()
    this.renderZoomLevel()
  }

  renderGraph = () => {
    const { rawPoints, height, metricsEnabled, rwgpsData } = this.props
    if (!this._div) { return }
    this._sampleGraph.resize(this._div.clientWidth, height)
    console.log('setting metricsEnabled')
    this._sampleGraph.metricsEnabled(metricsEnabled.toJS())
    this._sampleGraph.data(rwgpsData || parseRawTrackPoints(rawPoints))
    this._sampleGraph.render()
  }

  renderZoom = () => {
    const { zoomSlice } = this.state
    this._sampleGraph.zoomToDataSlice(zoomSlice)
  }

  renderZoomLevel = () => {
    const { zoomedIn } = this.props

    if (!this._div || !this._segmentSelector) { return }

    if (zoomedIn) {
      this._segmentSelector.reCenter()
    } else {
      if (!this._sampleGraph) { return }
      this._sampleGraph.zoomOutOnClick(null, false)
    }
  }

  renderHighlightSlice = () => {
    const { highlightSlice, zoomOnHighlightSliceChange, dragMode } = this.props
    if (highlightSlice) {
      const sliceWidth = highlightSlice[1] - highlightSlice[0]
      const dataSlice = this._sampleGraph.dataSlice(highlightSlice[0], 1 + sliceWidth)

      if (dragMode === 'zoom') {
        this._sampleGraph.zoomToDataSlice(dataSlice)
      } else {
        // dragmode is 'select'
        if (!this._segmentSelector) {
          // we're getting a double Segment Selector instance on the second selection after
          // a deselect here, probably on account of currentDragBehaviorFn being restored?
          console.log('SegmentSelector init')
          this._segmentSelector = new window.SampleGraph.SegmentSelector({ graph: this._sampleGraph, dataSlice: dataSlice })
          // undo the currentDragBehaviorFn changes that SegmentSelector performs on init
          this._sampleGraph._currentDragBehaviorFn = () => {}
        } else if (segOutOfSync(this._segmentSelector, dataSlice, 5)) {
          console.log(`SegmentSelector out of sync, correcting (expected: [${dataSlice.i}, ${dataSlice.lastI}] - actual [${this._segmentSelector.dataSlice.i}, ${this._segmentSelector.dataSlice.lastI}])`)
          this._segmentSelector.dataSlice = dataSlice
          this._segmentSelector.reposition()
        }
        if (zoomOnHighlightSliceChange) {
          this._segmentSelector.reCenter()
        }
      }
    } else {
      if (dragMode === 'zoom') {
        this._sampleGraph.unidentifySegment()
        this._sampleGraph.zoomOut()
      } else if (this._segmentSelector) {
        this.unloadSegmentSelector()
      }
    }
  }

  renderHoverSlice = () => {
    const { hoverSlice, highlightSlice } = this.props
    if (hoverSlice) {
      if (!sliceEqual(hoverSlice, highlightSlice)) {
        const sliceWidth = hoverSlice[1] - hoverSlice[0]
        const dataSlice = this._sampleGraph.dataSlice(hoverSlice[0], 1 + sliceWidth)
        this._sampleGraph.identifySegment(dataSlice)
      }
    } else {
      this._sampleGraph.unidentifySegment()
    }
  }

  unloadSegmentSelector = () => {
    this._segmentSelector.unload()
    // because segmentSelector.unload ref-binds currentDragBehaviorFn, we have to clobber again
    const sg = this._sampleGraph
    sg.unbindFrom(sg, 'segmentselected', sg._currentDragBehaviorFn)
    this._sampleGraph._currentDragBehaviorFn = () => {}
    delete this._segmentSelector
    this._segmentSelector = null
  }

  // there's definitely some UI jank here, which I think is the result of janky map
  // highlight point code perhaps? Need to do some CPU profiling on the mouseover
  // map/sg interaction
  renderHighlightPoint = () => {
    const { highlightPoint, highlightSlice, dragMode } = this.props
    const zoomedSlice = this._sampleGraph.zoomedSlice()

    if (highlightPoint) {
      if (
        zoomedSlice &&
        (zoomedSlice.i > highlightPoint.index || zoomedSlice.lastI < highlightPoint.index)
      ) {
        // if the map-highlighted point is out of the sample graph bounds, don't highlight it
        this._sampleGraph.defaultMouseOutCleanup()
        return
      }
      let offset = 0
      if (highlightSlice) {
        if (dragMode === 'zoom') {
          offset = highlightSlice[0]
        } else if (this._sampleGraph.zoomedIn()) {
          offset = this._sampleGraph.zoomedSlice().i
        }
      }
      this._sampleGraph.highlightPointByIndex(highlightPoint.index - offset, true)
    } else {
      this._sampleGraph.defaultMouseOutCleanup()
    }
  }

  onSegmentSelected = (segment) => {
    const { segmentFormat } = this.props
    if (this._segmentSelector) {
      // onSegmentSelected is fired whenever the user drag-selects a region on the graph
      // if there is no segment selector, then this should actually select/highlight the
      // segment, which is handled by props.onSegmentSelected and depending on mode, results
      // in a zoom or a segmentSelector instance.
      // If there is already a segmentSelector instance, then drag-select should result in a
      // zoom and it should not change the highlightSlice, which is what this zoomSlice state
      // is for. These names could be improved to aid in clarity.
      this.setState({ zoomSlice: segment })
      return
    }

    // bail out of this next part if we don't need to care about notifying the parent
    if (!this.props.onSegmentSelected) { return }

    // fire off a TrackLocation event if requested
    if (segmentFormat === 'TrackLocation') {
      this.props.onSegmentSelected([
        TrackLocation({ left: segment.i, percent: segment.pct1 }),
        TrackLocation({ left: segment.lastI, percent: segment.pct2 })
      ])
      return
    }

    // otherwise, do the legacy thing
    this.props.onSegmentSelected([segment.i, segment.i + segment.count], true)
  }

  // this also fires when a segment is selected?
  onSegmentResized = (segment) => {
    const { highlightSlice, highlightPoint } = this.props
    const sliceChanged = (
      (!highlightSlice) ||
      (highlightSlice[0] !== segment.i) ||
      (highlightSlice[1] !== segment.lastI)
    )

    if (highlightPoint) {
      this.props.onPointHighlighted &&
        this.props.onPointHighlighted(null)
    }

    // samplegraph is capable of returning 0-length segments, and we need to ignore them to prevent other components from throwing exceptions
    if (this.props.onSegmentSelected && sliceChanged && segment.i < segment.lastI) {
      if (this.props.segmentFormat === 'TrackLocation') {
        this.props.onSegmentSelected([
          TrackLocation({ left: segment.i, percent: segment.pct1 }),
          TrackLocation({ left: segment.lastI, percent: segment.pct2 })
        ])
      } else {
        this.props.onSegmentSelected([segment.i, segment.lastI])
      }
    }
  }

  onSegmentUnselected = () => {
    this.props.onSegmentUnselected &&
      this.props.onSegmentUnselected()
  }

  onPointHighlighted = (pointsAroundX) => {
    this.props.onPointHighlighted &&
      this.props.onPointHighlighted({ index: pointsAroundX.closestI })
  }

  suspendRendering = () => {
    this._suspendRendering = true
  }

  resumeRendering = () => {
    this._suspendRendering = false
  }

  clobberMetricPref = () => {
    try {
      window.rwgps.LabelMaker.metric = !!this.props.metricUnits
    } catch (probablyTypeError) {
      window.rwgps.LabelMaker = { metric: !!this.props.metricUnits }
    }
  }

  render () {
    return (
      <div id='sampleGraph' className={styles.SampleGraph}
        ref={r => { this._div = r }}
        onMouseOver={this.suspendRendering}
        onMouseOut={this.resumeRendering}
      >
        <canvas id={this._canvasId} />
      </div>
    )
  }
}

const outOfTolerance = (a, b, tolerance) => (
  Math.abs(a - b) > tolerance
)

// returns true if segmentSelector is off by more than +/- tolerance
const segOutOfSync = (segmentSelector, dataSlice, tolerance) => {
  try {
    const actual = segmentSelector.getUpdatedDataSlice()
    return (
      outOfTolerance(actual.i, dataSlice.i, tolerance) ||
      outOfTolerance(actual.lastI, dataSlice.lastI, tolerance)
    )
  } catch (e) {
    // this can happen if the segment selector is in a weird state, so we
    // default to forcing a resync
    return true
  }
}
